import React, { useEffect, useState } from "react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, StatTile } from "../../components/common/Primitives";
import api from "../../lib/api";

const EMPTY = {
  startups_by_sector: {},
  investors_by_type: {},
  startup_funnel: {},
  investor_funnel: {},
  deal_room_stages: {},
  signups_weekly: [],
  closed_deals: { count: 0, total_committed_inr: 0, total_success_fee_inr: 0 },
};

/** Small horizontal bar row used across sections. */
function Bar({ label, value, max, tone = "gold", testId }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const bg = tone === "navy" ? "bg-navy" : tone === "aqua" ? "bg-aqua-caribbean" : "bg-gold";
  return (
    <div className="mb-2" data-testid={testId}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-ink-soft">{label}</span>
        <span className="font-mono text-ink">{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-navy/[0.06] overflow-hidden">
        <div className={`h-full ${bg} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function crFmt(v) {
  if (!v) return "₹0";
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
}

export default function AdminReports() {
  const [data, setData] = useState(EMPTY);
  useEffect(() => { api.get("/admin/reports/summary").then((r) => setData({ ...EMPTY, ...(r.data || {}) })); }, []);

  const downloadCsv = (rows, name) => {
    const csv = "key,value\n" + Object.entries(rows).map(([k, v]) => `"${k}",${v}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
  };

  const downloadFullCsv = () => {
    const sections = [
      ["Startups by Sector", data.startups_by_sector],
      ["Investors by Type", data.investors_by_type],
      ["Startup Funnel", data.startup_funnel],
      ["Investor Funnel", data.investor_funnel],
      ["Deal Room Stages", data.deal_room_stages],
    ];
    let csv = "";
    for (const [title, rows] of sections) {
      csv += `\n${title}\nkey,value\n`;
      csv += Object.entries(rows || {}).map(([k, v]) => `"${k}",${v}`).join("\n") + "\n";
    }
    csv += `\nWeekly Signups\nweek_start,startups,investors\n`;
    csv += (data.signups_weekly || []).map((r) => `${r.week_start},${r.startups},${r.investors}`).join("\n") + "\n";
    csv += `\nClosed Deals\nkey,value\n`;
    csv += `count,${data.closed_deals?.count || 0}\n`;
    csv += `total_committed_inr,${data.closed_deals?.total_committed_inr || 0}\n`;
    csv += `total_success_fee_inr,${data.closed_deals?.total_success_fee_inr || 0}\n`;
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `venturaz-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const maxSector = Math.max(1, ...Object.values(data.startups_by_sector || {}));
  const maxInvType = Math.max(1, ...Object.values(data.investors_by_type || {}));
  const maxSignups = Math.max(1, ...(data.signups_weekly || []).flatMap((w) => [w.startups || 0, w.investors || 0]));
  const maxFunnel = Math.max(1, ...Object.values(data.startup_funnel || {}), ...Object.values(data.investor_funnel || {}));
  const maxStage = Math.max(1, ...Object.values(data.deal_room_stages || {}));

  return (
    <DashboardShell
      title="Reports"
      subtitle="ADMIN · ANALYTICS"
      actions={<button onClick={downloadFullCsv} className="text-[#8A6600] text-sm font-heading hover:underline" data-testid="report-export-all">Export All CSV</button>}
    >
      {/* Top-line KPIs — closed deal economics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatTile label="Deals Closed" value={data.closed_deals?.count || 0} testId="kpi-closed-count" />
        <StatTile label="Committed Capital" value={crFmt(data.closed_deals?.total_committed_inr)} testId="kpi-committed" />
        <StatTile label="Success Fee Earned" value={crFmt(data.closed_deals?.total_success_fee_inr)} testId="kpi-success-fee" />
        <StatTile label="Active Deal Rooms" value={(data.deal_room_stages?.["Interest Expressed"] || 0) + (data.deal_room_stages?.["Screening"] || 0) + (data.deal_room_stages?.["Due Diligence"] || 0) + (data.deal_room_stages?.["Term Sheet"] || 0) + (data.deal_room_stages?.["NDA Signed"] || 0)} testId="kpi-active-rooms" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Weekly signups timeline */}
        <Card testId="card-signups">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Signups · Last 8 Weeks</div>
            <button onClick={() => downloadCsv(Object.fromEntries((data.signups_weekly || []).map((w) => [w.week_start, `${w.startups}/${w.investors}`])), "weekly-signups.csv")} className="text-[#8A6600] text-xs font-heading">Export</button>
          </div>
          {(data.signups_weekly || []).length === 0 ? (
            <div className="text-ink-muted text-sm">No data.</div>
          ) : (
            <div className="space-y-3" data-testid="weekly-signups">
              {data.signups_weekly.map((w) => (
                <div key={w.week_start}>
                  <div className="flex justify-between text-xs mb-1 font-mono">
                    <span className="text-ink-muted">{w.week_start}</span>
                    <span className="text-ink">S {w.startups} · I {w.investors}</span>
                  </div>
                  <div className="flex gap-1 h-2">
                    <div className="bg-navy rounded-sm" style={{ width: `${(w.startups / maxSignups) * 50}%` }} />
                    <div className="bg-aqua-caribbean rounded-sm" style={{ width: `${(w.investors / maxSignups) * 50}%` }} />
                  </div>
                </div>
              ))}
              <div className="flex gap-4 text-[10px] font-mono text-ink-muted mt-2">
                <div className="flex items-center gap-1"><span className="w-2 h-2 bg-navy inline-block rounded-sm" /> Startups</div>
                <div className="flex items-center gap-1"><span className="w-2 h-2 bg-aqua-caribbean inline-block rounded-sm" /> Investors</div>
              </div>
            </div>
          )}
        </Card>

        {/* Deal room stages */}
        <Card testId="card-deal-stages">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Deal Room Stages</div>
            <button onClick={() => downloadCsv(data.deal_room_stages, "deal-room-stages.csv")} className="text-[#8A6600] text-xs font-heading">Export</button>
          </div>
          {Object.entries(data.deal_room_stages || {}).map(([k, v]) => (
            <Bar key={k} label={k} value={v} max={maxStage} tone="gold" testId={`stage-bar-${k.replace(/\s+/g, '-').toLowerCase()}`} />
          ))}
        </Card>

        {/* Startup funnel */}
        <Card testId="card-startup-funnel">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Startup Funnel</div>
            <button onClick={() => downloadCsv(data.startup_funnel, "startup-funnel.csv")} className="text-[#8A6600] text-xs font-heading">Export</button>
          </div>
          {Object.entries(data.startup_funnel || {}).map(([k, v]) => (
            <Bar key={k} label={k} value={v} max={maxFunnel} tone="navy" testId={`sfunnel-${k.toLowerCase().replace(/\s+/g,'-')}`} />
          ))}
        </Card>

        {/* Investor funnel */}
        <Card testId="card-investor-funnel">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Investor Funnel</div>
            <button onClick={() => downloadCsv(data.investor_funnel, "investor-funnel.csv")} className="text-[#8A6600] text-xs font-heading">Export</button>
          </div>
          {Object.entries(data.investor_funnel || {}).map(([k, v]) => (
            <Bar key={k} label={k} value={v} max={maxFunnel} tone="aqua" testId={`ifunnel-${k.toLowerCase().replace(/\s+/g,'-')}`} />
          ))}
        </Card>

        {/* Sector heatmap */}
        <Card testId="card-sectors">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Startups by Sector</div>
            <button onClick={() => downloadCsv(data.startups_by_sector, "startups-by-sector.csv")} className="text-[#8A6600] text-xs font-heading" data-testid="download-startups-report">Export CSV</button>
          </div>
          {Object.entries(data.startups_by_sector || {}).map(([k, v]) => (
            <Bar key={k} label={k} value={v} max={maxSector} tone="navy" testId={`sector-bar-${k.toLowerCase()}`} />
          ))}
        </Card>

        {/* Investor types */}
        <Card testId="card-investor-types">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Investors by Type</div>
            <button onClick={() => downloadCsv(data.investors_by_type, "investors-by-type.csv")} className="text-[#8A6600] text-xs font-heading" data-testid="download-investors-report">Export CSV</button>
          </div>
          {Object.entries(data.investors_by_type || {}).map(([k, v]) => (
            <Bar key={k} label={k} value={v} max={maxInvType} tone="aqua" testId={`invtype-bar-${k.toLowerCase().replace(/[^\w]+/g,'-')}`} />
          ))}
        </Card>
      </div>
    </DashboardShell>
  );
}
